/**
 * TimeWindowSelector (Phase 1 PR 9).
 *
 * Header-mounted control that drives the global time-window store.
 * Every Phase 1 page that renders volume/rate aggregates reads from
 * this store, so changing the selection here updates every visible
 * number across the app.
 *
 * Native <select> for keyboard accessibility + zero JS for the menu
 * itself. The Phase 1 design doesn't need anything more elaborate;
 * if we later want richer affordances (custom range, etc.) the
 * surface area to upgrade is small.
 */

import { Clock } from "lucide-react";
import {
  TIME_WINDOWS,
  useTimeWindowStore,
  type TimeWindow,
} from "@/lib/time-window";

const LABELS: Record<TimeWindow, string> = {
  "24h": "24h",
  "7d": "7d",
  "30d": "30d",
  "90d": "90d",
};

const FULL_LABELS: Record<TimeWindow, string> = {
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

export function TimeWindowSelector() {
  const value = useTimeWindowStore((s) => s.window);
  const setWindow = useTimeWindowStore((s) => s.setWindow);

  return (
    <label className="flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer text-slate-600">
      <Clock className="w-4 h-4 text-slate-400" aria-hidden="true" />
      <span className="sr-only">Time window</span>
      <select
        aria-label="Time window"
        value={value}
        onChange={(e) => setWindow(e.target.value as TimeWindow)}
        // bg-transparent + appearance-none so the native chevron
        // doesn't render inside our pill. The pill itself is the
        // affordance.
        className="bg-transparent appearance-none border-0 text-sm focus:outline-none focus:ring-0 cursor-pointer pr-1"
      >
        {TIME_WINDOWS.map((w) => (
          <option key={w} value={w}>
            {LABELS[w]}
          </option>
        ))}
      </select>
      <span className="sr-only">{FULL_LABELS[value]}</span>
    </label>
  );
}
